import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2, ArrowRight, Star, Puzzle, Dice5, Trophy, Heart, Diamond, Target, BookOpen, Search, HelpCircle, Plane, Shapes, Globe, Landmark, BarChart3, X, User, Crosshair, Sparkles, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const TIGER_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/373bd01ec6b7a90828c049b451f3a62da5129c3889c93c961a074588bd3baacb.png';

const TAJ_BG = 'https://images.unsplash.com/photo-1734966901559-0f401c2dbd75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxJbmRpYSUyMFRhaiUyME1haGFsJTIwY29sb3JmdWwlMjBpbGx1c3RyYXRpb258ZW58MHx8fHwxNzc2NDk2NDI1fDA&ixlib=rb-4.1.0&q=85';

const JUNGLE_BG = 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/ebe5cd94f0bfbe6b6a1b53df923c534aa03600247c3873b5a6b2ac52c32e3a20.png';

const GLOBE_BG = 'https://images.unsplash.com/photo-1546833998-07256bcc76ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxJbmRpYSUyMG1hcCUyMGNvbG9yZnVsJTIwaWxsdXN0cmF0aW9ufGVufDB8fHx8MTc3NzA0NDIxMXww&ixlib=rb-4.1.0&q=85';

const SKY_BG = 'https://images.unsplash.com/photo-1554976585-bf94aadbc26c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxFbWlyYXRlcyUyMGFpcnBsYW5lJTIwc21hbGwlMjBkaXN0YW50JTIwZmx5aW5nJTIwYmx1ZSUyMHNreXxlbnwwfHx8fDE3NzkyNjM3Nzh8MA&ixlib=rb-4.1.0&q=85';

const floatingIcons = [
  { Icon: Star, color: 'text-amber-300', size: 'w-12 h-12', top: '8%', left: '5%', delay: '0s', direction: 'right' },
  { Icon: Gamepad2, color: 'text-emerald-300', size: 'w-14 h-14', top: '15%', left: '85%', delay: '1.5s', direction: 'left' },
  { Icon: Puzzle, color: 'text-purple-300', size: 'w-11 h-11', top: '70%', left: '8%', delay: '0.8s', direction: 'right' },
  { Icon: Dice5, color: 'text-orange-300', size: 'w-13 h-13', top: '80%', left: '90%', delay: '2s', direction: 'left' },
  { Icon: Trophy, color: 'text-yellow-300', size: 'w-12 h-12', top: '45%', left: '3%', delay: '1.2s', direction: 'right' },
  { Icon: Heart, color: 'text-pink-300', size: 'w-10 h-10', top: '25%', left: '92%', delay: '0.3s', direction: 'left' },
  { Icon: Diamond, color: 'text-cyan-300', size: 'w-11 h-11', top: '55%', left: '93%', delay: '1.8s', direction: 'left' },
  { Icon: Target, color: 'text-red-300', size: 'w-12 h-12', top: '90%', left: '15%', delay: '2.5s', direction: 'right' },
  { Icon: Star, color: 'text-violet-300', size: 'w-10 h-10', top: '35%', left: '95%', delay: '0.6s', direction: 'left' },
  { Icon: Gamepad2, color: 'text-lime-300', size: 'w-11 h-11', top: '60%', left: '4%', delay: '1s', direction: 'right' },
  { Icon: Star, color: 'text-orange-200', size: 'w-9 h-9', top: '5%', left: '40%', delay: '2.2s', direction: 'right' },
  { Icon: Diamond, color: 'text-emerald-200', size: 'w-9 h-9', top: '92%', left: '55%', delay: '0.4s', direction: 'left' },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudioPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [nameShake, setNameShake] = useState(false);

  // Load saved name
  useEffect(() => {
    const saved = localStorage.getItem('gaurik_player_name');
    if (saved) {
      setPlayerName(saved);
      setNameInput(saved);
    }
  }, []);

  const saveName = () => {
    if (nameInput.trim()) {
      localStorage.setItem('gaurik_player_name', nameInput.trim());
      setPlayerName(nameInput.trim());
    }
  };

  const openAnalytics = () => {
    setShowAnalytics(true);
    setLoadingAnalytics(true);
    axios.get(`${API}/analytics`)
      .then((res) => setAnalyticsData(res.data))
      .catch(console.error)
      .finally(() => setLoadingAnalytics(false));
  };

  const handlePlayGame = (path) => {
    if (!playerName) {
      setNameShake(true);
      setTimeout(() => setNameShake(false), 600);
      return;
    }
    navigate(path);
  };

  const GAME_LABELS = {
    'jungle-math': 'Jungle Math',
    'gk-quiz': 'India GK Quiz',
    'who-am-i': 'Who Am I?',
    'airline-quiz': 'Airline Quiz',
    'shapes': 'Guess the Shape!',
    'country-quiz': 'Guess the Country!',
    'air-combat': "India vs Pakistan - Sky's Air Force",
    gods: 'Gods & Goddesses',
    monument: 'Guess the Monument!',
  };

  const games = [
    {
      id: 'jungle-math',
      title: 'Jungle Math',
      description: 'Grade 3 balloon math adventure',
      path: '/jungle-math',
      image: JUNGLE_BG,
      avatar: TIGER_URL,
      avatarType: 'image',
      buttonStyle: 'bg-gradient-to-r from-orange-400 to-red-500',
      hoverBorder: 'hover:border-orange-300',
      hoverShadow: 'hover:shadow-orange-200/40',
      ringColor: 'ring-orange-100',
    },
    {
      id: 'gk-quiz',
      title: 'India GK Quiz',
      description: 'Test your knowledge about India!',
      path: '/gk-quiz',
      image: TAJ_BG,
      avatarType: 'icon',
      avatarBg: 'linear-gradient(135deg, #FF9933, #138808)',
      AvatarIcon: BookOpen,
      buttonStyle: '',
      buttonBg: 'linear-gradient(90deg, #FF9933, #138808)',
      hoverBorder: 'hover:border-green-300',
      hoverShadow: 'hover:shadow-green-200/40',
      ringColor: 'ring-green-100',
    },
    {
      id: 'who-am-i',
      title: 'Who Am I?',
      description: 'Birds and animals of India.',
      path: '/who-am-i',
      image: GLOBE_BG,
      avatarType: 'icon',
      avatarBg: '#f59e0b',
      AvatarIcon: HelpCircle,
      buttonStyle: 'bg-gradient-to-r from-amber-400 to-orange-500',
      hoverBorder: 'hover:border-amber-300',
      hoverShadow: 'hover:shadow-amber-200/40',
      ringColor: 'ring-amber-100',
    },
    {
      id: 'airline-quiz',
      title: 'Airline Quiz',
      description: 'Guess the airline from the logo!',
      path: '/airline-quiz',
      image: SKY_BG,
      avatarType: 'icon',
      avatarBg: '#0ea5e9',
      AvatarIcon: Plane,
      buttonStyle: 'bg-gradient-to-r from-sky-400 to-indigo-500',
      hoverBorder: 'hover:border-sky-300',
      hoverShadow: 'hover:shadow-sky-200/40',
      ringColor: 'ring-sky-100',
    },
    {
      id: 'shapes',
      title: 'Guess the Shape!',
      description: 'Name the colourful shapes!',
      path: '/shapes',
      image: 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/b2f79a00e3e7026c8cc3c707837c5f6bc69c299688fbfbcfe5b474f82206f985.png',
      avatarType: 'icon',
      avatarBg: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
      AvatarIcon: Shapes,
      buttonStyle: 'bg-gradient-to-r from-pink-500 to-violet-500',
      hoverBorder: 'hover:border-pink-300',
      hoverShadow: 'hover:shadow-pink-200/40',
      ringColor: 'ring-pink-100',
      customBg: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 50%, #dbeafe 100%)',
    },
    {
      id: 'country-quiz',
      title: 'Guess the Country!',
      description: 'Hints from all continents!',
      path: '/country-quiz',
      image: 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/20bef7cbecd3d91aa84d00d85100cc053b4958b1fb4255cfd4f7f2b7146f0b51.png',
      avatarType: 'icon',
      avatarBg: 'linear-gradient(135deg, #10b981, #0d9488)',
      AvatarIcon: Globe,
      buttonStyle: 'bg-gradient-to-r from-emerald-500 to-teal-600',
      hoverBorder: 'hover:border-emerald-300',
      hoverShadow: 'hover:shadow-emerald-200/40',
      ringColor: 'ring-emerald-100',
    },
    {
      id: 'air-combat',
      title: "India vs Pakistan",
      description: "Sky's Air Force Game!",
      path: '/air-combat',
      image: 'https://images.unsplash.com/photo-1732800689337-688feb6a332b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxmaWdodGVyJTIwamV0JTIwZmx5aW5nJTIwYmx1ZSUyMHNreSUyMEluZGlhbiUyMEFpciUyMEZvcmNlfGVufDB8fHx8MTc4NjgxMTk3OHww&ixlib=rb-4.1.0&q=85',
      avatarType: 'icon',
      avatarBg: 'linear-gradient(135deg, #22c55e, #064e3b)',
      AvatarIcon: Crosshair,
      buttonStyle: 'bg-gradient-to-r from-green-600 to-emerald-800',
      hoverBorder: 'hover:border-green-400',
      hoverShadow: 'hover:shadow-green-200/40',
      ringColor: 'ring-green-200',
    },
    {
      id: 'gods',
      title: 'Gods & Goddesses',
      description: 'Hinduism, Christianity, Sikhism, Islam!',
      path: '/gods',
      image: 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/7b221cc89bb32c26551da609b6dc5932d3395b8dd600bcbd9048f845c7c2c510.jpeg',
      avatarType: 'icon',
      avatarBg: 'linear-gradient(135deg, #fbbf24, #ea580c)',
      AvatarIcon: Sparkles,
      buttonStyle: 'bg-gradient-to-r from-amber-500 to-orange-600',
      hoverBorder: 'hover:border-amber-300',
      hoverShadow: 'hover:shadow-amber-200/40',
      ringColor: 'ring-amber-100',
    },
    {
      id: 'monument',
      title: 'Guess the Monument!',
      description: 'Indian monuments with letter hints!',
      path: '/monument',
      image: 'https://images.unsplash.com/photo-1564507592333-c60657eea523?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxUYWolMjBNYWhhbCUyMEFncmF8ZW58MHx8fHwxNzkxNTYyMjg4fDA&ixlib=rb-4.1.0&q=85',
      avatarType: 'icon',
      avatarBg: 'linear-gradient(135deg, #a8a29e, #44403c)',
      AvatarIcon: Landmark,
      buttonStyle: 'bg-gradient-to-r from-stone-600 to-stone-800',
      hoverBorder: 'hover:border-stone-300',
      hoverShadow: 'hover:shadow-stone-200/40',
      ringColor: 'ring-stone-100',
    },
  ];

  const filtered = games.filter(
    (g) =>
      g.title.toLowerCase().includes(search.toLowerCase()) ||
      g.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white relative overflow-hidden flex flex-col">
      {/* Floating game icons */}
      {floatingIcons.map((item, i) => (
        <div
          key={`float-${item.top}-${item.left}`}
          className={`absolute ${item.color} opacity-40 ${item.direction === 'right' ? 'studio-drift-right' : 'studio-drift-left'}`}
          style={{ top: item.top, left: item.left, animationDelay: item.delay }}
        >
          <item.Icon className={item.size} strokeWidth={1.5} />
        </div>
      ))}

      {/* Scattered dots pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '30px 30px' }} />

      {/* Header */}
      <header className="relative z-10 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shadow-lg shadow-orange-300/30 rotate-3">
            <Gamepad2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1
              data-testid="studio-title"
              className="font-heading text-xl sm:text-2xl font-black text-gray-900 tracking-tight leading-none"
            >
              Gaurik Game Studio
            </h1>
            <p className="text-xs text-gray-400 font-body font-bold">Play. Learn. Grow.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="ad-free-badge"
            className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 font-body text-xs font-black px-3 py-1.5 rounded-full border border-emerald-200"
          >
            <ShieldCheck className="w-4 h-4" />
            100% Ad-Free
          </span>
          {/* Analytics button */}
          <button
            data-testid="open-analytics"
            onClick={openAnalytics}
            className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl transition-colors"
          >
            <BarChart3 className="w-5 h-5 text-gray-600" />
            <span className="font-body text-sm font-bold text-gray-600 hidden sm:inline">Analytics</span>
          </button>
        </div>
      </header>

      {/* Player name input */}
      <div className={`relative z-10 px-6 mb-2 ${nameShake ? 'animate-shake' : ''}`}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 max-w-xs">
            <User className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              data-testid="player-name-input"
              type="text"
              placeholder="Your name"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => e.key === 'Enter' && saveName()}
              className={`w-full py-1.5 px-3 rounded-xl border bg-white font-body text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-300 transition-all ${nameShake ? 'border-red-400 ring-2 ring-red-200' : 'border-gray-200'}`}
            />
          </div>
          {playerName && (
            <span className="font-body text-xs text-gray-400">
              Playing as <span className="font-bold text-gray-600">{playerName}</span>
            </span>
          )}
        </div>
        {!playerName && (
          <p className={`max-w-3xl mx-auto mt-1 font-body text-[11px] ${nameShake ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
            {nameShake ? 'Please enter your name first to play!' : 'Please write your name so who made this game will know your name.'}
          </p>
        )}
      </div>

      {/* Analytics Sidebar */}
      {showAnalytics && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowAnalytics(false)} />
          <div className="relative w-full max-w-sm bg-white shadow-2xl flex flex-col animate-slide-in-right">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-500" />
                <h2 className="font-heading text-lg font-black text-gray-900">Analytics</h2>
              </div>
              <button data-testid="close-analytics" onClick={() => setShowAnalytics(false)} className="p-1.5 rounded-full hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loadingAnalytics ? (
                <p className="text-center text-gray-400 font-body text-sm py-8">Loading...</p>
              ) : analyticsData.length === 0 ? (
                <p className="text-center text-gray-400 font-body text-sm py-8">No games played yet!</p>
              ) : (
                <div className="space-y-2">
                  {analyticsData.map((entry, i) => (
                    <div key={`${entry.game}-${entry.created_at}-${i}`} data-testid={`analytics-entry-${i}`} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-orange-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-sm font-bold text-gray-900 truncate">{entry.player_name}</p>
                        <p className="font-body text-xs text-gray-500">{GAME_LABELS[entry.game] || entry.game}</p>
                      </div>
                      <span className="font-heading text-sm font-black text-orange-500 shrink-0">
                        {entry.score}/{entry.total_questions}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="relative z-10 flex-1 px-6 pb-10 pt-4 max-w-3xl mx-auto w-full">
        {/* Search Bar */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            data-testid="game-search"
            type="text"
            placeholder="Search games..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-2 border-gray-100 bg-white font-body text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-300 focus:ring-4 focus:ring-orange-100 transition-all shadow-sm"
          />
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-2 gap-5">
          {filtered.map((game) => (
            <button
              key={game.id}
              data-testid={`launch-${game.id}`}
              onClick={() => handlePlayGame(game.path)}
              className={`group relative overflow-hidden rounded-3xl border-2 border-gray-100 ${game.hoverBorder} transition-all duration-500 shadow-lg hover:shadow-2xl ${game.hoverShadow} cursor-pointer bg-white text-left`}
            >
              <div
                className="h-36 sm:h-44 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={game.image ? { backgroundImage: `url(${game.image})` } : { background: game.customBg || '#f3f4f6' }}
              />
              <div className="relative p-5 flex flex-col items-center text-center -mt-10">
                {game.avatarType === 'image' ? (
                  <div className={`mb-2 w-16 h-16 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white group-hover:scale-110 transition-transform duration-500 ring-2 ${game.ringColor}`}>
                    <img src={game.avatar} alt={game.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className={`mb-2 w-16 h-16 rounded-full border-4 border-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-500 ring-2 ${game.ringColor}`} style={{ background: game.avatarBg }}>
                    <game.AvatarIcon className="w-8 h-8 text-white" />
                  </div>
                )}
                <h2 className="font-heading text-lg sm:text-xl font-black text-gray-900 mb-0.5">{game.title}</h2>
                <p className="font-body text-xs text-gray-500 mb-3">{game.description}</p>
                <div
                  className={`${game.buttonStyle} text-white font-bold text-sm py-2 px-6 rounded-full flex items-center gap-1.5 shadow-md group-hover:gap-3 transition-all`}
                  style={game.buttonBg ? { background: game.buttonBg } : {}}
                >
                  Play <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="font-body text-gray-400 text-base">No games found for &ldquo;{search}&rdquo;</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-4 text-center">
        <p className="font-body text-xs text-gray-300 flex items-center justify-center gap-1">
          Gaurik Game Studio <Star className="w-3 h-3 text-orange-300 fill-orange-300" /> Making learning fun
        </p>
      </footer>
    </div>
  );
}
